import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { globalLogout } from '@tt/hooks/globalLogout';

import { getCookie, removeCookie, setCookie } from '../utilities/cookie';
import { mockCookie, useMockCookie } from '../utilities/localAuth';
import phantom from '../utilities/phantom';

const CURRENT_VERSION = 7; // Increment this number when you make breaking changes

const stripAccountForPersist = (account) => {
    if (!account || typeof account !== 'object') return account;

    const { executionsLogs, ...persistedAccount } = account;
    return persistedAccount;
};

// Clears LocalStorage if user version is smaller than new version
const initializeLocalStorage = (storageKey, currentVersion) => {
    const storedState = JSON.parse(localStorage.getItem(storageKey));
    if (storedState?.version < currentVersion) {
        localStorage.removeItem(storageKey);
    }
};

const resetDateFilter = () => {
    const settingStorage = JSON.parse(localStorage.getItem('setting-storage'));
    const settingStorageFilter = settingStorage?.state?.filter;
    localStorage.removeItem('last-trade-day');
    localStorage.removeItem('first-trade-day');

    if (settingStorageFilter?.dateRange) {
        const newSettings = { ...settingStorageFilter, dateRange: null };
        localStorage.setItem(
            'setting-storage',
            JSON.stringify({
                ...settingStorage,
                state: { ...settingStorage.state, filter: newSettings }
            })
        );
    }
};

initializeLocalStorage('import-drawer', CURRENT_VERSION);
initializeLocalStorage('setting-storage', CURRENT_VERSION);
initializeLocalStorage('manager-storage', CURRENT_VERSION);
initializeLocalStorage('user-storage', CURRENT_VERSION);
resetDateFilter();

const useAccountStore = create(
    persist(
        (set) => ({
            account: {},
            allAccounts: [],
            allCommissions: [],
            selectedAccount: {},
            setSelectedAccount: (newAccount) =>
                set((state) => ({
                    selectedAccount: state.allAccounts.find((o) => o.key === newAccount?.key)
                })),
            setAllAccounts: (accounts) => set(() => ({ allAccounts: accounts })),
            changeAccount: (newSelectedAccount) =>
                set((state) => ({
                    account: state.allAccounts.find((o) => o.key === newSelectedAccount?.key)
                })),
            setAllCommissions: (commissions) => set(() => ({ allCommissions: commissions }))
        }),
        {
            name: 'user-storage',
            version: CURRENT_VERSION,
            partialize: (state) => ({
                account: stripAccountForPersist(state.account),
                allAccounts: state.allAccounts.map(stripAccountForPersist),
                allCommissions: state.allCommissions
            })
            // storage: createJSONStorage(() => sessionStorage),
            // onSet: (state) => {
            //   // Ensure that filter.accounts stays in sync with allAccounts
            //   state.filter.accounts = state.allAccounts;
            // },
        }
    )
);

const initialSettingStore = {
    filter: {
        assetType: ['Stock', 'Option', 'Future', 'Cash'],
        dateRange: null,
        tags: [],
        showAllTags: false,
        accounts: [],
        symbols: [],
        status: ['open', 'closed'],
        side: ['Long', 'Short'],
        basic_measure: 'Dollar',
        // TradeBook filters
        tradingPlanId: null,
        strategySetType: null,
        setNumber: null
    },
    advancedSettings: {
        options: {},
        executions: [],
        commissions: {},
        targetProfitAndStopLoss: {}
    },
    marks: []
};

const useSettingStore = create(
    persist(
        (set) => ({
            ...initialSettingStore,
            changeFilter: (key, value) =>
                set((state) => ({
                    filter: {
                        ...state.filter,
                        [key]: value
                    }
                })),
            setAdvancedSettings: (key, value) =>
                set((state) => ({
                    advancedSettings: {
                        ...state.advancedSettings,
                        [key]: value
                    }
                })),
            setMarks: (value) => set(() => ({ marks: value })),
            reset: () => set(initialSettingStore)
        }),

        {
            name: 'setting-storage',
            version: CURRENT_VERSION,
            partialize: (state) => ({
                filter: state.filter,
                advancedSettings: state.advancedSettings
            })
        }
    )
);

const USER_PAYMENT_STATUS = {
    notSubcribe: 0,
    trial: 1,
    active: 3
};

const JOURNAL_ALLOWED_PAYMENT_STATUSES = new Set([
    USER_PAYMENT_STATUS.trial,
    USER_PAYMENT_STATUS.active
]);

const EMPTY_USER = {
    name: '',
    email: '',
    lastName: '',
    avatar: '',
    token: '',
    darkMode: false,
    role: '',
    id: '',
    paymentStatus: USER_PAYMENT_STATUS.notSubcribe,
    remainDays: 0,
    paymentExpireDate: '',
    hasHadTrial: false,
    planStatus: ''
};

let storedUser = JSON.parse(getCookie('user') || 'false') || EMPTY_USER;
if (useMockCookie) {
    const parsedMockCookie = JSON.parse(mockCookie);
    setCookie('user', parsedMockCookie);
    storedUser = { ...EMPTY_USER, ...parsedMockCookie };
}

const normalizeValidatedSession = (sessionData, fallbackToken = '') => {
    const freshUser = sessionData?.user;
    const payment = sessionData?.payment || {};
    const token = sessionData?.token || fallbackToken || '';

    if (!freshUser || !token) return null;

    return {
        name: freshUser.first_name,
        email: freshUser.email,
        lastName: freshUser.last_name,
        avatar: freshUser.image,
        token,
        darkMode: freshUser.darkMode || false,
        role: freshUser.role,
        id: freshUser._id,
        paymentStatus: payment.status ?? USER_PAYMENT_STATUS.notSubcribe,
        remainDays: payment.remainDays ?? 0,
        paymentExpireDate: payment.expireDate ?? '',
        hasHadTrial: Boolean(freshUser.hasHadTrial),
        planStatus: freshUser.planStatus || ''
    };
};

const hasJournalAccess = (sessionData) =>
    Boolean(
        sessionData?.success === true &&
        sessionData?.user &&
        sessionData?.token &&
        JOURNAL_ALLOWED_PAYMENT_STATUSES.has(sessionData?.payment?.status)
    );

const initialManagementStore = {
    viewAs: {
        newUser: {
            token: {},
            id: {}
        },
        mainUser: {
            token: {},
            id: {}
        }
    },
    allUsers: [],
    company: 'Personal',
    adminPreferences: {
        selectedFundedType: 'SIMULATOR_TTF',
        lastFundedAccountId: null
    },
    publicView: {
        isActive: false,
        id: null
    }
};

const useManagementStore = create(
    persist(
        (set) => ({
            ...initialManagementStore,
            setViewAs: (newToken, mainToken) =>
                set(() => ({
                    viewAs: {
                        newUser: newToken,
                        mainUser: mainToken
                    }
                })),
            setPublicView: (isActive, id) =>
                set(() => ({
                    publicView: {
                        isActive,
                        id
                    }
                })),
            setAllUsers: (users) => set(() => ({ allUsers: users })),
            setCompany: (newCompany) => set(() => ({ company: newCompany })),
            setAdminSelectedType: (selectedFundedType) =>
                set((state) => ({
                    adminPreferences: {
                        ...state.adminPreferences,
                        selectedFundedType
                    }
                })),
            setLastFundedAccountId: (lastFundedAccountId) =>
                set((state) => ({
                    adminPreferences: {
                        ...state.adminPreferences,
                        lastFundedAccountId
                    }
                })),
            reset: () =>
                set((state) => ({
                    ...initialManagementStore,
                    adminPreferences:
                        state.adminPreferences || initialManagementStore.adminPreferences
                }))
        }),
        {
            name: 'manager-storage',
            version: CURRENT_VERSION,
            partialize: (state) => ({
                viewAs: state.viewAs,
                allUsers: state.allUsers,
                company: state.company,
                adminPreferences: state.adminPreferences
                // publicView: state.publicView
            })
        }
    )
);
// const storedUser = JSON.parse(
//   getCookie('user') || 'false'
// ) ||
//   { name: '',
//     email: '',
//     lastName: '',
//     avatar: '',
//     token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjYzNDcyMWMxYWQ2ODM1NGU5ODU2ZmUyMSIsImlhdCI6MTY5MTUwODIwMn0.oqB5Mw2q_1CJuhqb_NainYndcSw5sgIaStwSBepRDr4',
//     darkMode: false };

const userStore = create((set) => ({
    user: { ...storedUser },

    setUser: async ({
        name,
        email,
        lastName,
        avatar,
        token,
        darkMode,
        role,
        id,
        paymentStatus = USER_PAYMENT_STATUS.notSubcribe,
        remainDays = 0,
        paymentExpireDate = '',
        hasHadTrial = false,
        planStatus = ''
    }) => {
        const user = {
            name,
            email,
            lastName,
            avatar,
            token,
            darkMode,
            role,
            id,
            paymentStatus,
            remainDays,
            paymentExpireDate,
            hasHadTrial,
            planStatus
        };

        setCookie('user', user);

        return set(() => ({
            user
        }));
    },

    updateUser: ({ name, lastName }) =>
        set((state) => ({
            user: { ...state.user, name, lastName }
        })),

    updateToken: ({ token }) =>
        set((state) => ({
            user: { ...state.user, token }
        })),

    updateDarkMode: ({ mode }) =>
        set((state) => ({
            user: { ...state.user, darkMode: mode }
        })),

    logoutUser: () =>
        globalLogout({
            token: userStore.getState().user.token,
            onCleanup: () => userStore.getState().resetUser()
        }),

    resetUser: () => {
        removeCookie('user');

        return set(() => ({
            user: {
                ...EMPTY_USER
            }
        }));
    }
}));

function useUserState() {
    const user = userStore((state) => state.user);

    const isUserAuthenticated = async () => {
        if (user.token === '') return false;

        const isValid = await phantom
            .get('/user/validateToken/', {
                headers: { token: user.token }
            })
            .then(({ data }) => hasJournalAccess(data))
            .catch(() => false);

        return isValid;
    };

    const updateDarkMode = userStore((state) => state.updateDarkMode);

    const setUser = userStore((state) => state.setUser);

    const updateUser = userStore((state) => state.updateUser);

    const updateToken = userStore((state) => state.updateToken);

    const resetUser = userStore((state) => state.resetUser);
    const logoutUser = userStore((state) => state.logoutUser);

    const getUser = () => ({
        name: user.name,
        email: user.email,
        lastName: user.lastName,
        avatar: user.avatar,
        token: user.token,
        darkMode: user.darkMode,
        role: user.role,
        id: user.id,
        paymentStatus: user.paymentStatus,
        remainDays: user.remainDays,
        paymentExpireDate: user.paymentExpireDate,
        hasHadTrial: user.hasHadTrial,
        planStatus: user.planStatus
    });

    // Used for refreshing user info on page load; in case role or any other user info has changed
    const refreshUser = async (tokenOverride = user.token) => {
        const sessionData = await phantom
            .get('/user/validateToken/', {
                headers: { token: tokenOverride }
            })
            .then(({ data }) => data);

        const normalizedUser = normalizeValidatedSession(sessionData, tokenOverride);

        if (normalizedUser) {
            await setUser(normalizedUser);
        }

        return sessionData;
    };

    const resetManagementStore = useManagementStore((state) => state.reset);
    const resetSettingStore = useSettingStore((state) => state.reset);

    const resetSiteConfigs = async () => {
        resetManagementStore();
        resetSettingStore();
    };

    return {
        getUser,
        isUserAuthenticated,
        setUser,
        resetUser,
        logoutUser,
        updateUser,
        updateToken,
        updateDarkMode,
        refreshUser,
        resetSiteConfigs,
        hasJournalAccess
    };
}

export { useAccountStore, useSettingStore, userStore, useManagementStore, useUserState };
export { useTradeBookStore } from './tradebookStore';
